import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState
} from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { Button, Card, OverlayTrigger, Tooltip } from 'react-bootstrap';
import { Link, useSearchParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faAt,
  faClipboardCheck,
  faClipboardList,
  faClock,
  faEye,
  faInfoCircle,
  faLayerGroup,
  faPhoneAlt,
  faRedoAlt,
  faSyncAlt
} from '@fortawesome/free-solid-svg-icons';
import AdvanceTable from 'components/common/advance-table/AdvanceTable';
import AdvanceTableSearchBox from 'components/common/advance-table/AdvanceTableSearchBox';
import CommonBreadCrumb from 'components/commonbreadcrumb/CommonBreadCrumb';
import RegistrationQueueShimmer from 'components/ShimmerUI/RegistrationQueueShimmer';
import useAdvanceTable from 'hooks/useAdvanceTable';
import useRequestState from 'hooks/useRequestState';
import AdvanceTableProvider from 'providers/AdvanceTableProvider';
import paths from 'routes/paths';
import {
  NETWORK_ERROR_MESSAGE,
  fetchRegistrationCount,
  getApiErrorMessage,
  listRegistrations
} from 'helpers/registrationApi';
import {
  REGISTRATION_STATUS_TABS,
  STATUS_META,
  announceRegistrationsChanged,
  canManageEmailDomains,
  getLoggedInRoleId,
  getRoleMeta,
  isKnownStatusTab
} from 'helpers/registrationMeta';
import {
  getCachedFacilityNameMap,
  getCachedGroupNameMap
} from 'components/admin/facility-staff/add-facility-staff/staffFormRules';
import {
  FacilityChips,
  InitialsAvatar,
  RolePill,
  StateMessage,
  StatusPill,
  TonePill,
  ToneIcon
} from 'components/registration-shared/RegistrationUI';
import {
  QueueEmptyIllustration,
  QueueErrorIllustration
} from 'components/registration-shared/RegistrationIllustrations';
import {
  formatDateTime,
  formatPhone,
  formatRelative,
  getAgeInHours,
  getFullName,
  getRequestedFacilities,
  getRequestedRoleId,
  getStatus,
  getSubmittedAt,
  getTypedOrganization,
  hasGroup,
  toMoment
} from 'components/registration-shared/registrationFormat';
import ApproveRegistrationModal from './ApproveRegistrationModal';
import QueuePagination from './QueuePagination';
import RegistrationDetailDrawer from './RegistrationDetailDrawer';
import RejectRegistrationModal from './RejectRegistrationModal';

const DEFAULT_LIMIT = 25;
const LEAVE_ANIMATION_MS = 320;

const TONE_COLORS = {
  warning: '#f5803e',
  success: '#22b35e',
  danger: '#e63757',
  primary: '#2c7be5'
};

// Pending requests get warmer colours the longer they wait.
const getAgeTone = (record, status) => {
  if (status !== 'pending') {
    return 'secondary';
  }
  const hours = getAgeInHours(getSubmittedAt(record));
  if (hours === null) {
    return 'secondary';
  }
  if (hours < 24) {
    return 'success';
  }
  return hours < 72 ? 'info' : 'warning';
};

// Narrow screens drop the less critical columns (the drawer shows everything);
// below `md` the applicant cell carries role / status / age inline instead.
const hideBelow = breakpoint => `d-none d-${breakpoint}-table-cell`;

const AgePill = ({ record }) => {
  const submittedAt = getSubmittedAt(record);
  if (!submittedAt) {
    return <span className="text-400">—</span>;
  }
  return (
    <OverlayTrigger
      placement="top"
      overlay={
        <Tooltip style={{ position: 'fixed' }}>
          {formatDateTime(submittedAt)}
        </Tooltip>
      }
    >
      <span className="d-inline-flex">
        <TonePill tone={getAgeTone(record, getStatus(record))} icon={faClock}>
          {formatRelative(submittedAt)}
        </TonePill>
      </span>
    </OverlayTrigger>
  );
};

AgePill.propTypes = { record: PropTypes.object.isRequired };

const CountValue = ({ value }) => {
  if (typeof value === 'number') {
    return value;
  }
  if (value === false) {
    return (
      <span className="mym-count-missing" aria-label="Count unavailable">
        —
      </span>
    );
  }
  return <span className="mym-count-placeholder" aria-label="Loading" />;
};

CountValue.propTypes = {
  value: PropTypes.oneOfType([PropTypes.number, PropTypes.bool])
};

/**
 * Tracks whether the table scrolls sideways (and whether it's scrolled to the
 * end) so the pinned actions column only casts its edge shadow when rows are
 * actually passing underneath it.
 */
const useHorizontalScrollState = (containerRef, active) => {
  const [state, setState] = useState({ scrollable: false, atEnd: true });

  useEffect(() => {
    const scroller = active
      ? containerRef.current?.querySelector('.table-responsive')
      : null;
    if (!scroller) {
      setState({ scrollable: false, atEnd: true });
      return undefined;
    }
    const measure = () => {
      const scrollable = scroller.scrollWidth > scroller.clientWidth + 1;
      const atEnd =
        scroller.scrollLeft + scroller.clientWidth >= scroller.scrollWidth - 1;
      setState(prev =>
        prev.scrollable === scrollable && prev.atEnd === atEnd
          ? prev
          : { scrollable, atEnd }
      );
    };
    measure();
    scroller.addEventListener('scroll', measure, { passive: true });
    const observer =
      typeof ResizeObserver === 'undefined'
        ? null
        : new ResizeObserver(measure);
    if (observer) {
      observer.observe(scroller);
      const tableEl = scroller.querySelector('table');
      if (tableEl) {
        observer.observe(tableEl);
      }
    }
    return () => {
      scroller.removeEventListener('scroll', measure);
      if (observer) {
        observer.disconnect();
      }
    };
  }, [containerRef, active]);

  return state;
};

/**
 * Self-registration approval queue — /main/admin/registrations (U3 / U4).
 * Group admins and pharmacy roles (1–4) review who asked for access, then
 * approve (pre-filled, editable, creates the account) or reject (with a
 * reason). The server scopes what each role sees; a role-4 admin only ever
 * gets their own group's rows.
 *
 * One way in per row: a pending request opens straight into the review
 * screen, where approve and reject both live; anything already decided opens
 * read-only in the detail drawer.
 */
const RegistrationQueue = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const requestedTab = String(
    searchParams.get('status') || 'pending'
  ).toLowerCase();
  const status = isKnownStatusTab(requestedTab) ? requestedTab : 'pending';
  const statusMeta = STATUS_META[status];

  const roleId = getLoggedInRoleId();
  const showGroupColumn = roleId <= 3; // role 4 only ever sees its own group
  // Without the Organization column there's room for Phone a breakpoint sooner.
  const phoneColumnClass = hideBelow(showGroupColumn ? 'xxl' : 'xl');

  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(DEFAULT_LIMIT);
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [loadedKey, setLoadedKey] = useState(null);
  const [counts, setCounts] = useState({
    pending: null,
    approved: null,
    rejected: null
  });
  const [leavingIds, setLeavingIds] = useState([]);
  const [drawer, setDrawer] = useState({ show: false, record: null });
  const [approve, setApprove] = useState({ show: false, record: null });
  const [reject, setReject] = useState({
    show: false,
    record: null,
    fromReview: false
  });
  const tableCardRef = useRef(null);

  const listReq = useRequestState({ initialStatus: 'loading' });
  const { run: runList } = listReq;
  const requestKey = `${status}|${page}|${limit}`;

  /* --------------------------------- lookups -------------------------------- */

  const facilityNames = useMemo(() => getCachedFacilityNameMap(), []);
  const groupNames = useMemo(() => getCachedGroupNameMap(), []);

  const lookupFacility = useCallback(
    id => facilityNames.get(String(id)) || '',
    [facilityNames]
  );

  const getGroupName = useCallback(
    record => {
      if (record?.group_name) {
        return record.group_name;
      }
      if (!hasGroup(record)) {
        return '—';
      }
      const cached = groupNames.get(String(record.group_id));
      if (cached) {
        return cached;
      }
      return roleId === 4 ? 'Your group' : `Group #${record.group_id}`;
    },
    [groupNames, roleId]
  );

  /* ---------------------------------- data ---------------------------------- */

  const loadList = useCallback(async () => {
    const result = await runList(() =>
      listRegistrations({ status, page, limit })
    );
    if (!result.ok) {
      return;
    }
    const { rows: nextRows, total: nextTotal } = result.data;
    // A page emptied by approvals / rejections: step back one page.
    if (nextRows.length === 0 && page > 1) {
      setPage(nextTotal > 0 ? Math.max(1, Math.ceil(nextTotal / limit)) : 1);
      return;
    }
    setRows(nextRows);
    setTotal(nextTotal);
    setLoadedKey(`${status}|${page}|${limit}`);
    if (status !== 'all') {
      setCounts(prev => ({ ...prev, [status]: nextTotal }));
    }
    if (status === 'pending') {
      announceRegistrationsChanged({ pending: nextTotal });
    }
  }, [runList, status, page, limit]);

  const loadCounts = useCallback(async () => {
    const tabs = ['pending', 'approved', 'rejected'];
    const results = await Promise.allSettled(
      tabs.map(tab => fetchRegistrationCount(tab))
    );
    // A failed count keeps the last number we had; with none it shows "—"
    // (false) rather than a placeholder that would pulse forever.
    setCounts(prev => {
      const next = {};
      tabs.forEach((tab, index) => {
        const result = results[index];
        if (result.status === 'fulfilled' && typeof result.value === 'number') {
          next[tab] = result.value;
        } else {
          next[tab] = typeof prev[tab] === 'number' ? prev[tab] : false;
        }
      });
      return next;
    });
    const pendingResult = results[0];
    if (
      pendingResult.status === 'fulfilled' &&
      typeof pendingResult.value === 'number'
    ) {
      announceRegistrationsChanged({ pending: pendingResult.value });
    }
  }, []);

  useEffect(() => {
    loadList();
  }, [loadList]);

  useEffect(() => {
    loadCounts();
  }, [loadCounts]);

  const refreshAll = () => {
    loadList();
    loadCounts();
  };

  const changeStatus = next => {
    if (next === status) {
      return;
    }
    setSearchParams(next === 'pending' ? {} : { status: next }, {
      replace: true
    });
    setPage(1);
  };

  /* --------------------------- after approve / reject ----------------------- */

  // U4.6: the row leaves at once (with a short slide-out), counts update
  // optimistically, then the page and counts are re-fetched to stay truthful.
  const settleRow = (recordId, nextStatus) => {
    const id = String(recordId);

    setCounts(prev => ({
      ...prev,
      pending:
        typeof prev.pending === 'number'
          ? Math.max(0, prev.pending - 1)
          : prev.pending,
      [nextStatus]:
        typeof prev[nextStatus] === 'number'
          ? prev[nextStatus] + 1
          : prev[nextStatus]
    }));
    if (typeof counts.pending === 'number') {
      announceRegistrationsChanged({
        pending: Math.max(0, counts.pending - 1)
      });
    }

    if (status === 'all') {
      setRows(prev =>
        prev.map(row =>
          String(row.id) === id ? { ...row, status: nextStatus } : row
        )
      );
    } else {
      setLeavingIds(prev => [...prev, id]);
      setTimeout(() => {
        setRows(prev => prev.filter(row => String(row.id) !== id));
        setTotal(prev => Math.max(0, prev - 1));
        setLeavingIds(prev => prev.filter(leavingId => leavingId !== id));
      }, LEAVE_ANIMATION_MS);
    }

    setTimeout(() => {
      loadList();
      loadCounts();
    }, LEAVE_ANIMATION_MS + 80);
  };

  const handleApproved = ({ record, payload, response }) => {
    setApprove(prev => ({ ...prev, show: false }));
    const name = `${payload.first_name} ${payload.last_name}`.trim();
    const emailSent = response.email_sent;

    toast.success(
      <div>
        <div className="fw-bold">{name} approved</div>
        <div className="fs-10">
          Account created
          {emailSent === true
            ? ` — welcome email sent to ${payload.email}.`
            : '.'}
        </div>
      </div>,
      { theme: 'colored' }
    );

    // R4.5: SMTP trouble never fails an approval — so say so loudly, otherwise
    // everyone assumes the applicant got their set-password link (K4).
    if (emailSent === false || emailSent === 0 || emailSent === 'false') {
      toast.warning(
        <div>
          <div className="fw-bold">The welcome email wasn't sent</div>
          <div className="fs-10 mt-1">
            {name}'s account was created, but the email to {payload.email}{' '}
            didn't go out. Ask them to use “Forgot password” on the sign-in page
            to set a password.
          </div>
        </div>,
        { theme: 'colored', autoClose: false, closeOnClick: false }
      );
    }

    if (Number(payload.role_id) === 7 && response.user_id) {
      toast.info(
        <div className="fs-10">
          SharedLiving caregivers need patients assigned before they can work.{' '}
          <Link
            className="fw-bold text-white text-decoration-underline"
            to={paths.userSharedLivingCaregiverDetail.replace(
              ':user_id',
              response.user_id
            )}
          >
            Assign patients now
          </Link>
        </div>,
        { theme: 'colored', autoClose: 12000 }
      );
    }

    settleRow(record.id, 'approved');
  };

  const handleRejected = ({ record }) => {
    setReject(prev => ({ ...prev, show: false, fromReview: false }));
    toast.success(`Request from ${getFullName(record)} rejected.`, {
      theme: 'colored'
    });
    settleRow(record.id, 'rejected');
  };

  // Pending → the review screen (approve or reject there); decided → details.
  const openRecord = record =>
    getStatus(record) === 'pending'
      ? setApprove({ show: true, record })
      : setDrawer({ show: true, record });

  const openReject = record => {
    setApprove(prev => ({ ...prev, show: false }));
    setReject({ show: true, record, fromReview: true });
  };

  // Cancelling a reject that started on the review screen goes back to it.
  const closeReject = () => {
    setReject(prev => ({ ...prev, show: false, fromReview: false }));
    if (reject.fromReview && reject.record) {
      setApprove({ show: true, record: reject.record });
    }
  };

  /* --------------------------------- columns -------------------------------- */

  const columns = [
    {
      id: 'applicant',
      accessorFn: row => `${getFullName(row)} ${row.email || ''}`,
      header: 'Applicant',
      meta: {
        headerProps: { className: 'text-900 ps-3' },
        cellProps: { className: 'ps-3' }
      },
      cell: ({ row: { original } }) => (
        <div className="mym-applicant-cell d-flex align-items-center gap-2">
          <InitialsAvatar record={original} />
          <div className="min-w-0">
            <button
              type="button"
              className="mym-applicant-name d-block text-truncate mw-100"
              onClick={() => openRecord(original)}
            >
              {getFullName(original)}
            </button>
            <div className="mym-applicant-email text-truncate">
              {original.email}
            </div>
            <div className="d-flex d-md-none flex-wrap align-items-center gap-1 mt-1">
              <RolePill roleId={getRequestedRoleId(original)} />
              {status === 'all' && <StatusPill status={getStatus(original)} />}
              <AgePill record={original} />
            </div>
          </div>
        </div>
      )
    },
    {
      id: 'phone',
      accessorFn: row => formatPhone(row.phone),
      header: 'Phone',
      meta: {
        headerProps: { className: `text-900 ${phoneColumnClass}` },
        cellProps: { className: phoneColumnClass }
      },
      cell: ({ getValue }) =>
        getValue() ? (
          <span className="text-nowrap">
            <FontAwesomeIcon
              icon={faPhoneAlt}
              className="text-500 me-2 fs-11"
            />
            {getValue()}
          </span>
        ) : (
          <span className="text-400">—</span>
        )
    },
    ...(showGroupColumn
      ? [
          {
            id: 'group',
            // No group yet: show (and search) what the applicant typed.
            accessorFn: row =>
              hasGroup(row) || row?.group_name
                ? getGroupName(row)
                : getTypedOrganization(row) || '—',
            header: 'Organization',
            meta: {
              headerProps: { className: `text-900 ${hideBelow('xl')}` },
              cellProps: { className: hideBelow('xl') }
            },
            cell: ({ getValue, row: { original } }) => {
              const typed =
                !hasGroup(original) && !original?.group_name
                  ? getTypedOrganization(original)
                  : '';
              if (!typed) {
                return (
                  <span className="text-nowrap">
                    <FontAwesomeIcon
                      icon={faLayerGroup}
                      className="text-500 me-2 fs-11"
                    />
                    {getValue()}
                  </span>
                );
              }
              return (
                <OverlayTrigger
                  placement="top"
                  overlay={
                    <Tooltip style={{ position: 'fixed' }}>
                      Entered by the applicant — not matched to a group yet.
                    </Tooltip>
                  }
                >
                  <span className="text-nowrap fst-italic text-700">
                    <FontAwesomeIcon
                      icon={faLayerGroup}
                      className="text-400 me-2 fs-11"
                    />
                    {typed}
                  </span>
                </OverlayTrigger>
              );
            }
          }
        ]
      : []),
    {
      id: 'role',
      accessorFn: row => getRoleMeta(getRequestedRoleId(row)).label,
      header: 'Requested role',
      meta: {
        headerProps: { className: `text-900 ${hideBelow('md')}` },
        cellProps: { className: hideBelow('md') }
      },
      cell: ({ row: { original } }) => (
        <RolePill roleId={getRequestedRoleId(original)} />
      )
    },
    {
      id: 'facilities',
      accessorFn: row =>
        getRequestedFacilities(row, lookupFacility)
          .map(facility => facility.name)
          .join(', '),
      header: () => (
        <span className="text-nowrap">
          Requested facilities
          <OverlayTrigger
            placement="top"
            overlay={
              <Tooltip style={{ position: 'fixed' }}>
                What the applicant asked for. Nothing is granted until you
                approve.
              </Tooltip>
            }
          >
            <span className="ms-1">
              <FontAwesomeIcon icon={faInfoCircle} className="text-500" />
            </span>
          </OverlayTrigger>
        </span>
      ),
      enableSorting: false,
      meta: {
        headerProps: { className: `text-900 ${hideBelow('lg')}` },
        cellProps: { className: hideBelow('lg') }
      },
      cell: ({ row: { original } }) => (
        <FacilityChips
          facilities={getRequestedFacilities(original, lookupFacility)}
          max={2}
        />
      )
    },
    {
      id: 'submitted',
      accessorFn: row => toMoment(getSubmittedAt(row))?.valueOf() ?? 0,
      header: 'Submitted',
      meta: {
        headerProps: { className: `text-900 ${hideBelow('md')}` },
        cellProps: { className: hideBelow('md') }
      },
      cell: ({ row: { original } }) => <AgePill record={original} />
    },
    ...(status === 'all'
      ? [
          {
            id: 'status',
            accessorFn: row => getStatus(row),
            header: 'Status',
            meta: {
              headerProps: { className: `text-900 ${hideBelow('md')}` },
              cellProps: { className: hideBelow('md') }
            },
            cell: ({ row: { original } }) => (
              <StatusPill status={getStatus(original)} />
            )
          }
        ]
      : []),
    {
      id: 'actions',
      header: () => <span className="visually-hidden">Actions</span>,
      enableSorting: false,
      // Pinned to the right edge so the actions never scroll out of reach.
      meta: {
        headerProps: { className: 'text-end pe-3 mym-sticky-end' },
        cellProps: { className: 'text-end pe-3 mym-sticky-end' }
      },
      // One action per row; approve and reject live on the review screen.
      cell: ({ row: { original } }) => {
        const pending = getStatus(original) === 'pending';
        return (
          <Button
            size="sm"
            variant={pending ? 'falcon-primary' : 'falcon-default'}
            className="text-nowrap"
            onClick={() => openRecord(original)}
            disabled={leavingIds.includes(String(original.id))}
            aria-label={`${
              pending ? 'Review' : 'View'
            } request from ${getFullName(original)}`}
          >
            <FontAwesomeIcon
              icon={pending ? faClipboardCheck : faEye}
              className="me-1"
            />
            {pending ? 'Review' : 'View'}
          </Button>
        );
      }
    }
  ];

  const table = useAdvanceTable({
    data: rows,
    columns,
    selection: false,
    sortable: true,
    pagination: true,
    // Paging happens on the server; show the whole fetched page.
    perPage: 1000
  });

  /* --------------------------------- render --------------------------------- */

  const hasCurrentData = loadedKey === requestKey;
  const isInitialLoading = listReq.isLoading && !hasCurrentData;
  const isRefreshing = listReq.isLoading && hasCurrentData;
  const tabCounts = [counts.pending, counts.approved, counts.rejected];
  let allCount = null;
  if (tabCounts.every(count => typeof count === 'number')) {
    allCount = counts.pending + counts.approved + counts.rejected;
  } else if (tabCounts.includes(false)) {
    allCount = false;
  }
  const visibleRowCount = table.getRowModel().rows.length;

  const breadcrumbItems = [
    { name: 'Dashboard', href: '/main', isActive: false },
    { name: 'User Management', href: paths.viewFacility, isActive: false },
    {
      name: 'Registration Requests',
      href: paths.adminRegistrations,
      isActive: true
    }
  ];

  const showTable =
    !isInitialLoading &&
    !(listReq.isError && !hasCurrentData) &&
    rows.length > 0;
  const tableScroll = useHorizontalScrollState(tableCardRef, showTable);

  let content;
  if (isInitialLoading) {
    content = <RegistrationQueueShimmer rows={Math.min(limit, 6)} />;
  } else if (listReq.isError && !hasCurrentData) {
    content = (
      <StateMessage
        art={<QueueErrorIllustration width={170} />}
        title="We couldn't load registration requests"
        actions={
          <Button variant="primary" onClick={refreshAll}>
            <FontAwesomeIcon icon={faRedoAlt} className="me-2" />
            Try again
          </Button>
        }
      >
        {getApiErrorMessage(listReq.error, NETWORK_ERROR_MESSAGE)}
      </StateMessage>
    );
  } else if (rows.length === 0) {
    content = (
      <StateMessage
        art={
          <QueueEmptyIllustration
            width={170}
            mark={status === 'pending' ? 'check' : 'dots'}
            accent={TONE_COLORS[statusMeta.tone]}
          />
        }
        title={statusMeta.emptyTitle}
      >
        {statusMeta.emptyText}
      </StateMessage>
    );
  } else {
    content = (
      <>
        {listReq.isError && (
          <div className="px-3 pt-3">
            <div className="mym-inline-note mym-tone-warning">
              <FontAwesomeIcon icon={faInfoCircle} className="mt-1" />
              <div className="flex-1">
                Couldn't refresh — showing the last loaded list.{' '}
                <Button
                  variant="link"
                  size="sm"
                  className="p-0 align-baseline fw-semibold"
                  onClick={refreshAll}
                >
                  Try again
                </Button>
              </div>
            </div>
          </div>
        )}
        <AdvanceTable
          headerClassName="bg-200 text-nowrap align-middle"
          rowClassName={row =>
            classNames('align-middle', {
              'mym-row-leaving': leavingIds.includes(String(row.original.id))
            })
          }
          tableProps={{
            size: 'sm',
            striped: true,
            className: 'fs-10 mb-0'
          }}
        />
        {visibleRowCount === 0 && (
          <div className="text-center text-600 fs-10 py-4">
            No requests on this page match your search.
          </div>
        )}
      </>
    );
  }

  return (
    <div className="mym-queue">
      <CommonBreadCrumb breadcrumbItems={breadcrumbItems} />

      <Card className="mym-queue-hero mb-3 shadow-sm">
        <Card.Body className="p-3 p-md-4">
          <div className="d-flex flex-wrap align-items-center gap-3 mb-3 mb-md-4">
            <ToneIcon
              icon={faClipboardList}
              tone="primary"
              size="xl"
              solid
              className="d-none d-sm-inline-flex"
            />
            <div className="flex-1 min-w-0">
              <h1 className="mym-queue-title">Registration requests</h1>
              <p className="mym-queue-sub">
                Access requests from the registration page. Approving creates
                the account and emails a link to set a password.
              </p>
            </div>
            {canManageEmailDomains(roleId) && (
              <Button
                as={Link}
                to={paths.adminEmailDomains}
                variant="falcon-default"
                size="sm"
                aria-label="Email domains"
              >
                <FontAwesomeIcon icon={faAt} className="me-sm-2 text-primary" />
                <span className="d-none d-sm-inline">Email domains</span>
              </Button>
            )}
            <Button
              variant="falcon-default"
              size="sm"
              onClick={refreshAll}
              disabled={listReq.isLoading}
              aria-label="Refresh"
            >
              <FontAwesomeIcon
                icon={faSyncAlt}
                spin={listReq.isLoading}
                className="me-sm-2"
              />
              <span className="d-none d-sm-inline">Refresh</span>
            </Button>
          </div>

          <div
            className="mym-stat-tabs"
            role="tablist"
            aria-label="Filter requests by status"
          >
            {REGISTRATION_STATUS_TABS.map(tab => {
              const meta = STATUS_META[tab];
              const count = tab === 'all' ? allCount : counts[tab];
              const active = status === tab;
              return (
                <button
                  key={tab}
                  type="button"
                  role="tab"
                  aria-selected={active}
                  className={classNames(
                    'mym-stat-tab',
                    `mym-tone-${meta.tone}`,
                    {
                      'is-active': active
                    }
                  )}
                  onClick={() => changeStatus(tab)}
                >
                  <ToneIcon icon={meta.icon} tone={meta.tone} solid={active} />
                  <span className="d-flex flex-column min-w-0">
                    <span className="mym-stat-tab-label">{meta.label}</span>
                    <span className="mym-stat-tab-count">
                      <CountValue value={count} />
                    </span>
                  </span>
                </button>
              );
            })}
          </div>
        </Card.Body>
      </Card>

      <AdvanceTableProvider {...table}>
        <Card
          ref={tableCardRef}
          className={classNames(
            'mym-queue-table shadow-sm overflow-hidden mb-3',
            {
              'is-x-scrollable': tableScroll.scrollable,
              'is-x-end': tableScroll.atEnd
            }
          )}
        >
          <Card.Header className="d-flex flex-wrap align-items-center justify-content-between gap-2 py-3">
            <div className="d-flex align-items-center gap-2">
              <ToneIcon
                icon={statusMeta.icon}
                tone={statusMeta.tone}
                size="sm"
              />
              <h2 className="mb-0 fs-9 fw-bold text-900">
                {status === 'all'
                  ? 'All requests'
                  : `${statusMeta.label} requests`}
              </h2>
              {hasCurrentData && (
                <TonePill tone={statusMeta.tone}>{total}</TonePill>
              )}
            </div>
            <div className="flex-grow-1" style={{ maxWidth: '20rem' }}>
              <AdvanceTableSearchBox placeholder="Search this page…" />
            </div>
          </Card.Header>
          <div
            className={classNames('mym-refresh-bar', {
              'is-idle': !isRefreshing
            })}
            aria-hidden="true"
          />
          <Card.Body className="p-0">{content}</Card.Body>
          {hasCurrentData && total > 0 && (
            <Card.Footer className="border-top py-2">
              <QueuePagination
                page={page}
                limit={limit}
                total={total}
                disabled={listReq.isLoading}
                onPageChange={setPage}
                onLimitChange={next => {
                  setLimit(next);
                  setPage(1);
                }}
              />
            </Card.Footer>
          )}
        </Card>
      </AdvanceTableProvider>

      <RegistrationDetailDrawer
        show={drawer.show}
        record={drawer.record}
        onHide={() => setDrawer(prev => ({ ...prev, show: false }))}
        getGroupName={getGroupName}
        lookupFacility={lookupFacility}
      />
      <ApproveRegistrationModal
        show={approve.show}
        record={approve.record}
        onHide={() => setApprove(prev => ({ ...prev, show: false }))}
        onApproved={handleApproved}
        onReject={openReject}
        getGroupName={getGroupName}
        lookupFacility={lookupFacility}
      />
      <RejectRegistrationModal
        show={reject.show}
        record={reject.record}
        onHide={closeReject}
        onRejected={handleRejected}
      />
    </div>
  );
};

export default RegistrationQueue;
